import { useMutation } from 'react-query'

import agent from 'agent'
import useAuth from 'hooks/use-auth'
import {
  type UseIntegrationInvocationRunTestEndpointOptions,
  type UseIntegrationInvocationRunTestEndpointReturnType,
  type RunTestEndpointParams,
  type RunTestEndpointResponse
} from 'queries/external-integrations/types'
import {
  getEndpointWithParams,
  throwIfEmptyResponse
} from 'queries/queries-utils'
import { throwIfBadResponseForRunTestEndpoint } from 'queries/status-checks/query-utils'

const getEndpointByInvocationId = (invocationId: string) =>
  `integration-invocations/${invocationId}/run-test-endpoint`

function runTestEndpoint(teamId: string, signal?: AbortSignal) {
  return async function ([invocationId, requestParams]: RunTestEndpointParams) {
    const endpoint = getEndpointByInvocationId(invocationId)
    const endpointWithParams = getEndpointWithParams(endpoint, { teamId })
    const response = await agent.post<RunTestEndpointResponse>(
      endpointWithParams,
      { ...requestParams },
      { signal }
    )

    throwIfEmptyResponse(response)
    throwIfBadResponseForRunTestEndpoint(response)

    return response.data
  }
}

// Export function type signatures
export function useIntegrationInvocationsRunTestEndpoint(
  signal?: AbortSignal,
  options?: UseIntegrationInvocationRunTestEndpointOptions
): UseIntegrationInvocationRunTestEndpointReturnType

export function useIntegrationInvocationsRunTestEndpoint(
  options?: UseIntegrationInvocationRunTestEndpointOptions
): UseIntegrationInvocationRunTestEndpointReturnType

/**
 * Submit a POST request to test the endpoint configuration for an Integration Invocation.
 * This is primarily used for PNI types, and requires a Webhook Invocation first be created.
 */
export function useIntegrationInvocationsRunTestEndpoint(
  ...args:
    | [AbortSignal?, UseIntegrationInvocationRunTestEndpointOptions?]
    | [UseIntegrationInvocationRunTestEndpointOptions?]
) {
  const { currentTeamId: teamId } = useAuth()
  const [signalOrOptions, maybeOptions] = args
  const hasSignal = signalOrOptions instanceof AbortSignal

  return useMutation({
    ...(maybeOptions ?? signalOrOptions),
    mutationFn: runTestEndpoint(teamId, hasSignal ? signalOrOptions : undefined)
  })
}
